// Page admin — Réglages globaux AUPRÉA
import { useState, useEffect } from "react";
import { C, Btn, Input, Textarea, FormField, Alert, Spinner, useAsync, reglagesApi } from "./shared";
import { OngletContrats } from "./ReglagesContrats";
import { MotifsRefusReglage, RelancesReglage } from "./IntegrationsAdmin";
import { DelaiSignatureReglage } from "./ContratsAdmin";
import { FormulesReglage, RappelsReglage } from "./CotisationsAdminV2";
import { PurgeTest } from "./PurgeTest";
import { ReglagePostdc, ReglageRecapNotifs } from "./NotificationsAdmin";

const TABS = [
  { key: "contrats",   label: "Société & Contrats" },
  { key: "fiscal",     label: "Fiscal & Paiement" },
  { key: "commissions",label: "Commissions" },
  { key: "relances",   label: "Relances & Validité" },
  { key: "rib",        label: "RIB AUPRÉA" },
  { key: "textes",     label: "Textes (CGV / RGPD / Relance)" },
  { key: "leads",      label: "Leads" },
  { key: "integration", label: "Intégration" },
  { key: "cotisations", label: "Cotisations" },
  { key: "prestations", label: "Prestations" },
  { key: "notifications", label: "Notifications" },
  { key: "purge", label: "Données de test" },
];

const Card = ({ title, subtitle, children }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
    {title && <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 4 }}>{title}</div>}
    {subtitle && <div style={{ fontSize: 12, color: C.mid, marginBottom: 14 }}>{subtitle}</div>}
    {children}
  </div>
);

const SaveBar = ({ saving, dirty, onSave, ok }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
    <Btn variant="gold" disabled={!dirty || saving} onClick={onSave} data-testid="reglages-save">
      {saving ? "Enregistrement…" : "Enregistrer"}
    </Btn>
    {ok && <span style={{ fontSize: 12, color: C.green }}>✓ Sauvegardé</span>}
    {dirty && !ok && <span style={{ fontSize: 12, color: C.amber }}>Modifications non enregistrées</span>}
  </div>
);

// ── Onglet Fiscal & Paiement ────────────────────────────────────────────────
const OngletFiscal = ({ r, save }) => {
  const [tva, setTva] = useState(r.tva_taux);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const dirty = tva !== r.tva_taux;
  const doSave = async () => {
    setSaving(true); setOk(false);
    await save({ tva_taux: Number(tva) });
    setSaving(false); setOk(true); setTimeout(() => setOk(false), 2500);
  };
  return (
    <Card title="Fiscalité & moyens de paiement" subtitle="Taux appliqué aux prix HT et règles de paiement.">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <FormField label="Taux de TVA appliqué aux prix HT (%)">
          <Input type="number" step="0.1" value={tva} onChange={(e) => setTva(e.target.value)} data-testid="reglages-tva" />
        </FormField>
      </div>
      <Alert type="info"><span>ℹ</span><span>Paiement en une fois par défaut (carte ou virement). Le paiement en 2 fois (50 % à la signature, 50 % au jalon) n'est possible que sur accord de l'admin, demandé par le consultant dans la proposition.</span></Alert>
      <SaveBar saving={saving} dirty={dirty} ok={ok} onSave={doSave} />
    </Card>
  );
};

// ── Onglet Commissions ──────────────────────────────────────────────────────
const OngletCommissions = ({ r, save }) => {
  const [p, setP] = useState(r.paliers_commission);
  const [ov, setOv] = useState(r.override_reseau);
  const [ab, setAb] = useState(r.commission_abonnement);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const dirty = JSON.stringify(p) !== JSON.stringify(r.paliers_commission)
             || JSON.stringify(ov) !== JSON.stringify(r.override_reseau)
             || ab !== r.commission_abonnement;
  const doSave = async () => {
    setSaving(true); setOk(false);
    await save({
      paliers_commission: { ...p, starter: Number(p.starter), confirme: Number(p.confirme), expert: Number(p.expert), seuil_confirme: Number(p.seuil_confirme), seuil_expert: Number(p.seuil_expert) },
      override_reseau:    { n1: Number(ov.n1), n2: Number(ov.n2), n3: Number(ov.n3) },
      commission_abonnement: Number(ab),
    });
    setSaving(false); setOk(true); setTimeout(() => setOk(false), 2500);
  };
  const pct = (v) => (Number(v) * 100).toFixed(1);
  return (
    <>
      <Card title="Paliers de commission (paiement unique)" subtitle="Taux par palier + seuils de ventes du mois (calcul par tranche).">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 12 }}>
          <FormField label="Starter (fraction, ex 0.20 = 20%)">
            <Input type="number" step="0.01" value={p.starter} onChange={(e) => setP({ ...p, starter: e.target.value })} data-testid="reglages-starter" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(p.starter)} %</div>
          </FormField>
          <FormField label="Confirmé">
            <Input type="number" step="0.01" value={p.confirme} onChange={(e) => setP({ ...p, confirme: e.target.value })} data-testid="reglages-confirme" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(p.confirme)} %</div>
          </FormField>
          <FormField label="Expert">
            <Input type="number" step="0.01" value={p.expert} onChange={(e) => setP({ ...p, expert: e.target.value })} data-testid="reglages-expert" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(p.expert)} %</div>
          </FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <FormField label="Seuil Confirmé (n-ième vente du mois)">
            <Input type="number" value={p.seuil_confirme} onChange={(e) => setP({ ...p, seuil_confirme: e.target.value })} data-testid="reglages-seuil-confirme" />
          </FormField>
          <FormField label="Seuil Expert (n-ième vente du mois)">
            <Input type="number" value={p.seuil_expert} onChange={(e) => setP({ ...p, seuil_expert: e.target.value })} data-testid="reglages-seuil-expert" />
          </FormField>
        </div>
      </Card>

      <Card title="Override réseau (N1 / N2 / N3)" subtitle="Rémunération des parrains sur les ventes des filleuls.">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          <FormField label="N1 (parrain direct)">
            <Input type="number" step="0.01" value={ov.n1} onChange={(e) => setOv({ ...ov, n1: e.target.value })} data-testid="reglages-n1" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(ov.n1)} %</div>
          </FormField>
          <FormField label="N2">
            <Input type="number" step="0.01" value={ov.n2} onChange={(e) => setOv({ ...ov, n2: e.target.value })} data-testid="reglages-n2" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(ov.n2)} %</div>
          </FormField>
          <FormField label="N3">
            <Input type="number" step="0.01" value={ov.n3} onChange={(e) => setOv({ ...ov, n3: e.target.value })} data-testid="reglages-n3" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(ov.n3)} %</div>
          </FormField>
        </div>
      </Card>

      <Card title="Commission abonnements Carnet Digital" subtitle="Taux mensuel versé au consultant sur chaque abonnement actif.">
        <FormField label="Commission mensuelle (fraction)">
          <Input type="number" step="0.01" value={ab} onChange={(e) => setAb(e.target.value)} data-testid="reglages-abo" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>≈ {pct(ab)} %</div>
        </FormField>
        <SaveBar saving={saving} dirty={dirty} ok={ok} onSave={doSave} />
      </Card>
    </>
  );
};

// ── Onglet Relances ─────────────────────────────────────────────────────────
const OngletRelances = ({ r, save }) => {
  const [j1, setJ1] = useState(r.relances_jours[0] ?? 2);
  const [j2, setJ2] = useState(r.relances_jours[1] ?? 7);
  const [val, setVal] = useState(r.validite_proposition_jours);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const dirty = j1 !== (r.relances_jours[0] ?? 2) || j2 !== (r.relances_jours[1] ?? 7) || val !== r.validite_proposition_jours;
  const doSave = async () => {
    setSaving(true); setOk(false);
    await save({ relances_jours: [Number(j1), Number(j2)].sort((a,b) => a-b), validite_proposition_jours: Number(val) });
    setSaving(false); setOk(true); setTimeout(() => setOk(false), 2500);
  };
  return (
    <Card title="Relances automatiques & validité proposition" subtitle="Ces valeurs pilotent le cron d'envoi (Étape 6) et le compte à rebours affiché au client.">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
        <FormField label="1ʳᵉ relance (jours après envoi)">
          <Input type="number" value={j1} onChange={(e) => setJ1(e.target.value)} data-testid="reglages-relance1" />
        </FormField>
        <FormField label="2ᵉ relance (jours après envoi)">
          <Input type="number" value={j2} onChange={(e) => setJ2(e.target.value)} data-testid="reglages-relance2" />
        </FormField>
        <FormField label="Validité d'une proposition (jours)">
          <Input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="reglages-validite" />
        </FormField>
      </div>
      <SaveBar saving={saving} dirty={dirty} ok={ok} onSave={doSave} />
    </Card>
  );
};

// ── Onglet RIB ──────────────────────────────────────────────────────────────
const OngletRib = ({ r, save }) => {
  const [rib, setRib] = useState(r.rib);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const dirty = JSON.stringify(rib) !== JSON.stringify(r.rib);
  const doSave = async () => {
    if (!window.confirm("Modifier le RIB déclenchera une entrée dans le journal et une alerte e-mail admin. Confirmer ?")) return;
    setSaving(true); setOk(false);
    await save({ rib });
    setSaving(false); setOk(true); setTimeout(() => setOk(false), 2500);
  };
  const { data: journal, loading, reload } = useAsync(reglagesApi.journalRib, []);
  return (
    <>
      <Card title="Coordonnées bancaires AUPRÉA" subtitle="Modification réservée à l'admin, journalisée + e-mail d'alerte automatique.">
        <Alert type="warn"><span>⚠</span><span>Toute modification est loggée et déclenchera un e-mail d'alerte (activé automatiquement dès la mise en place de Resend).</span></Alert>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 16 }}>
          <FormField label="IBAN">
            <Input value={rib.iban} onChange={(e) => setRib({ ...rib, iban: e.target.value })} data-testid="reglages-iban" />
          </FormField>
          <FormField label="BIC">
            <Input value={rib.bic} onChange={(e) => setRib({ ...rib, bic: e.target.value })} data-testid="reglages-bic" />
          </FormField>
          <FormField label="Titulaire">
            <Input value={rib.titulaire} onChange={(e) => setRib({ ...rib, titulaire: e.target.value })} data-testid="reglages-titulaire" />
          </FormField>
        </div>
        <SaveBar saving={saving} dirty={dirty} ok={ok} onSave={async () => { await doSave(); reload(); }} />
      </Card>

      <Card title="Journal des modifications RIB" subtitle="Historique horodaté pour audit.">
        {loading ? <Spinner /> :
          (journal && journal.length > 0) ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.gray2 }}>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Date</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Par</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Champ modifié</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Ancienne valeur</th>
                  <th style={{ textAlign: "left", padding: "8px 12px", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>Nouvelle valeur</th>
                </tr>
              </thead>
              <tbody>
                {journal.flatMap((j, i) => {
                  const fields = [
                    { key: "iban", label: "IBAN" },
                    { key: "bic",  label: "BIC" },
                    { key: "titulaire", label: "Titulaire" },
                  ];
                  const changes = fields.filter(f => (j.old?.[f.key] || "") !== (j.new?.[f.key] || ""));
                  if (changes.length === 0) return [];
                  return changes.map((f, k) => (
                    <tr key={`${i}-${f.key}`} style={{ borderTop: `.5px solid ${C.border}` }}>
                      {k === 0 && <td rowSpan={changes.length} style={{ padding: "8px 12px", color: C.mid, verticalAlign: "top" }}>{new Date(j.at).toLocaleString("fr-FR")}</td>}
                      {k === 0 && <td rowSpan={changes.length} style={{ padding: "8px 12px", color: C.mid, verticalAlign: "top" }}>{j.by || "—"}</td>}
                      <td style={{ padding: "8px 12px", color: C.mid, fontWeight: 600 }}>{f.label}</td>
                      <td style={{ padding: "8px 12px", color: C.mid, fontFamily: "monospace" }}>{j.old?.[f.key] || "—"}</td>
                      <td style={{ padding: "8px 12px", color: C.navy, fontFamily: "monospace", fontWeight: 600 }}>{j.new?.[f.key] || "—"}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          ) : <div style={{ fontSize: 12, color: C.muted, padding: 12 }}>Aucune modification enregistrée.</div>
        }
      </Card>
    </>
  );
};

// ── Onglet Leads (Étape 16) ─────────────────────────────────────────────────
const LEAD_PARAMS = [
  ["doublon_jours", "Antériorité d'un lead (jours sans action avant qu'un autre consultant puisse le déclarer)"],
  ["relance_jours", "Badge « À relancer » après (jours sans action)"],
  ["dormant_jours", "Lead dormant signalé à l'admin après (jours sans action)"],
  ["anonymisation_ans", "Anonymisation automatique après (années sans action)"],
];
const OngletLeads = ({ r, save }) => {
  const [v, setV] = useState({ doublon_jours: 90, relance_jours: 7, dormant_jours: 30, anonymisation_ans: 3, ...(r.leads || {}) });
  const [ok, setOk] = useState(false);
  const doSave = async () => { await save({ leads: v }); setOk(true); setTimeout(() => setOk(false), 2500); };
  return (
    <Card title="Règles des leads" subtitle="Doublons, alertes d'inactivité et conservation RGPD.">
      <div style={{ display: "grid", gap: 12, maxWidth: 560 }}>
        {LEAD_PARAMS.map(([k, label]) => (
          <FormField key={k} label={label}>
            <Input type="number" min={1} value={v[k]} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })} data-testid={`reglages-leads-${k}`} />
          </FormField>
        ))}
      </div>
      <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <Btn variant="gold" onClick={doSave} data-testid="reglages-leads-save">Enregistrer</Btn>
        {ok && <span style={{ fontSize: 12, color: C.green }}>✓ Enregistré</span>}
      </div>
    </Card>
  );
};

// ── Onglet Textes ───────────────────────────────────────────────────────────
const OngletTextes = ({ r, save }) => {
  const [t, setT] = useState(r.textes);
  const [saving, setSaving] = useState(false);
  const [ok, setOk] = useState(false);
  const dirty = JSON.stringify(t) !== JSON.stringify(r.textes);
  const doSave = async () => {
    setSaving(true); setOk(false);
    await save({ textes: t });
    setSaving(false); setOk(true); setTimeout(() => setOk(false), 2500);
  };
  const isPlaceholder = (s) => (s || "").startsWith("À REMPLACER");
  return (
    <Card title="Textes légaux et messages" subtitle="Bandeau « À REMPLACER » affiché tant que le texte définitif n'a pas été fourni.">
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <FormField label={<>CGV {isPlaceholder(t.cgv) && <span style={{ background: C.amberlt, color: C.amber, padding: "1px 6px", borderRadius: 4, fontSize: 9, marginLeft: 6 }}>À REMPLACER</span>}</>}>
          <Textarea value={t.cgv} onChange={(e) => setT({ ...t, cgv: e.target.value })} style={{ minHeight: 100 }} data-testid="reglages-cgv" />
        </FormField>
        <FormField label={<>Mention de consentement RGPD {isPlaceholder(t.rgpd) && <span style={{ background: C.amberlt, color: C.amber, padding: "1px 6px", borderRadius: 4, fontSize: 9, marginLeft: 6 }}>À REMPLACER</span>}</>}>
          <Textarea value={t.rgpd} onChange={(e) => setT({ ...t, rgpd: e.target.value })} style={{ minHeight: 80 }} data-testid="reglages-rgpd" />
        </FormField>
        <FormField label="Mention de transparence (recommandation de partenaires)">
          <Textarea value={t.transparence || ""} onChange={(e) => setT({ ...t, transparence: e.target.value })} style={{ minHeight: 70 }} data-testid="reglages-transparence" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Affichée au client lors du choix d'un partenaire et reprise dans le rapport de synthèse.</div>
        </FormField>
        <FormField label={<>Message de relance {isPlaceholder(t.relance) && <span style={{ background: C.amberlt, color: C.amber, padding: "1px 6px", borderRadius: 4, fontSize: 9, marginLeft: 6 }}>À REMPLACER</span>}</>}>
          <Textarea value={t.relance} onChange={(e) => setT({ ...t, relance: e.target.value })} style={{ minHeight: 100 }} data-testid="reglages-relance-txt" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Variables disponibles : <code>{"{prenom}"}</code>, <code>{"{formule}"}</code>, <code>{"{lien}"}</code>, <code>{"{consultant}"}</code></div>
        </FormField>
      </div>
      <SaveBar saving={saving} dirty={dirty} ok={ok} onSave={doSave} />
    </Card>
  );
};

// ── Page principale ─────────────────────────────────────────────────────────
export const ReglagesAdmin = () => {
  const [reglages, setReglages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("fiscal");
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    reglagesApi.get().then(setReglages).finally(() => setLoading(false));
  }, []);

  if (loading || !reglages) return <Spinner />;

  const save = async (patch) => {
    setErr("");
    try {
      const fresh = await reglagesApi.update(patch);
      setReglages(fresh);
      setToast("✓ Modifications enregistrées");
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  return (
    <>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, background: C.green, color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, boxShadow: "0 8px 24px rgba(0,0,0,.2)", zIndex: 300 }} data-testid="reglages-toast">
          {toast}
        </div>
      )}
      <div style={{ display: "flex", gap: 4, borderBottom: `.5px solid ${C.border}`, marginBottom: 20, overflowX: "auto" }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`reglages-tab-${t.key}`}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              padding: "10px 16px", fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? C.navy : C.mid,
              borderBottom: tab === t.key ? `2px solid ${C.gold}` : "2px solid transparent",
              fontFamily: "inherit", whiteSpace: "nowrap",
            }}>{t.label}</button>
        ))}
      </div>
      {tab === "contrats"    && <OngletContrats    r={reglages} save={save} />}
      {tab === "fiscal"      && <OngletFiscal      r={reglages} save={save} />}
      {tab === "commissions" && <OngletCommissions r={reglages} save={save} />}
      {tab === "relances"    && <OngletRelances    r={reglages} save={save} />}
      {tab === "rib"         && <OngletRib         r={reglages} save={save} />}
      {tab === "textes"      && <OngletTextes      r={reglages} save={save} />}
      {tab === "leads"       && <OngletLeads       r={reglages} save={save} />}
      {tab === "integration" && <><RelancesReglage /><MotifsRefusReglage /><DelaiSignatureReglage /></>}
      {tab === "cotisations" && <><FormulesReglage /><RappelsReglage /></>}
      {tab === "prestations" && <ReglagePostdc />}
      {tab === "notifications" && <ReglageRecapNotifs />}
      {tab === "purge" && <PurgeTest />}
    </>
  );
};
